import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { analyticsService } from '../api/analytics';
import { EventNames } from '../types/analytics';
import { DownloadStatus } from '../types/downloads';
import { ExamTypes } from '../types/examgenerator';

const DownloadErrorCodes = {
  unknown: 'UNKNOWN',
  none: 'NONE',
  fileNotCreated: 'FILE_NOT_CREATED',
  connectionTimedOut: 'CONNECTION_TIMED_OUT',
  unknownHost: 'UNKNOWN_HOST',
  httpNotFound: 'HTTP_NOT_FOUND',
  writePermissionDenied: 'WRITE_PERMISSION_DENIED',
  noStorageSpace: 'NO_STORAGE_SPACE',
  noNetworkConnection: 'NO_NETWORK_CONNECTION',
  emptyResponseFromServer: 'EMPTY_RESPONSE_FROM_SERVER',
  requestAlreadyExist: 'REQUEST_ALREADY_EXIST',
  downloadNotFound: 'DOWNLOAD_NOT_FOUND',
  fetchDatabaseError: 'FETCH_DATABASE_ERROR',
  requestWithIdAlreadyExist: 'REQUEST_WITH_ID_ALREADY_EXIST',
  unknownIoError: 'UNKNOWN_IO_ERROR',
  fileNotFound: 'FILE_NOT_FOUND',
  fetchFileServerUrlInvalid: 'FETCH_FILE_SERVER_URL_INVALID',
  invalidContentHash: 'INVALID_CONTENT_HASH',
  failedToUpdateRequest: 'FAILED_TO_UPDATE_REQUEST',
  failedToAddCompletedDownload: 'FAILED_TO_ADD_COMPLETED_DOWNLOAD',
  fetchFileServerInvalidResponse: 'FETCH_FILE_SERVER_INVALID_RESPONSE',
  requestDoesNotExist: 'REQUEST_DOES_NOT_EXIST',
  enqueueNotSuccessful: 'ENQUEUE_NOT_SUCCESSFUL',
  completedNotAddedSuccessfully: 'COMPLETED_NOT_ADDED_SUCCESSFULLY',
  enqueuedRequestsAreNotDistinct: 'ENQUEUED_REQUESTS_ARE_NOT_DISTINCT',
  failedToRenameIncompleteDownloadFile:
    'FAILED_TO_RENAME_INCOMPLETE_DOWNLOAD_FILE',
  failedToRenameFile: 'FAILED_TO_RENAME_FILE',
  fileAllocationFailed: 'FILE_ALLOCATION_FAILED',
  httpConnectionNotAllowed: 'HTTP_CONNECTION_NOT_ALLOWED',
};

export const subjectIcons = [
  { id: 3, label: 'English', icon: '../../assets/english.svg' },
  { id: 1, label: 'Maths', icon: '../../assets/math.svg' },
  { id: 4, label: 'General Science', icon: '../../assets/science.svg' },
  {
    id: 5,
    label: 'Waqfiyat Amaa',
    icon: '../../assets/general-knowledge.svg',
  },
  { id: 2, label: 'Social Studies', icon: '../../assets/social-studies.svg' },
  { id: 11, label: 'Islamiyat', icon: '../../assets/islamiat.svg' },
  { id: 9, label: 'Urdu', icon: '../../assets/urdu.svg' },
  {
    id: 7,
    label: 'Physical Education',
    icon: '../../assets/physical-education.svg',
  },
  {
    id: 8,
    label: 'Extra Curricular',
    icon: '../../assets/extra-curricular.svg',
  },
  {
    id: 10,
    label: 'Reading Hour Urdu',
    icon: '../../assets/reading-hour-urdu.svg',
  },
  {
    id: 12,
    label: 'Reading Hour English',
    icon: '../../assets/reading-hour-english.svg',
  },
  {
    id: 13,
    label: 'Numeracy',
    icon: '../../assets/numeracy.svg',
  },
  {
    id: 14,
    label: 'Computer Science',
    icon: '../../assets/computer_science.svg',
  },
  {
    id: 15,
    label: 'Physics',
    icon: '../../assets/physics.svg',
  },
  {
    id: 16,
    label: 'Chemistry',
    icon: '../../assets/chemistry.svg',
  },
  {
    id: 17,
    label: 'Biology',
    icon: '../../assets/biology.svg',
  },
];

export const Days = [
  { id: 1, name: 'Monday', initials: 'M' },
  { id: 2, name: 'Tuesday', initials: 'T' },
  { id: 3, name: 'Wednesday', initials: 'W' },
  { id: 4, name: 'Thursday', initials: 'T' },
  { id: 5, name: 'Friday', initials: 'F' },
  { id: 6, name: 'Saturday', initials: 'S' },
];
export const sections = [
  { label: 'A', id: 1 },
  { label: 'B', id: 2 },
  { label: 'C', id: 3 },
  { label: 'D', id: 4 },
  { label: 'E', id: 5 },
  { label: 'F', id: 6 },
  { label: 'G', id: 7 },
  { label: 'H', id: 8 },
];
export const getMediaCurrentProgress = (asset: any): number | null => {
  if (asset?.download) {
    return Math.floor(
      (asset?.download.downloaded / asset?.download.total) * 100,
    );
  } else return null;
};

export const getMediaStatus = (
  asset: any,
): { status: string; errorCode: string } | null => {
  const errorCodes = Object.values(DownloadErrorCodes);
  if (
    asset?.download?.status === DownloadStatus.Failed &&
    errorCodes.includes(asset.download.error)
  ) {
    return {
      status: asset?.download?.status,
      errorCode: asset?.download?.error,
    };
  }
  if (asset?.download?.status) {
    return {
      status: asset?.download?.status,
      errorCode: asset?.download?.error,
    };
  } else
    return {
      status: DownloadStatus.None,
      errorCode: DownloadStatus.None,
    };
};
export const openInAppBrowser = async (url: string) => {
  if (!Capacitor.isNativePlatform()) {
    window.open(url, '_self');
  } else {
    await Browser.open({
      url: url,
    });
  }

  analyticsService.trackEvent(EventNames.helpClicked);
};

export const getExamTypeLabel = (
  examType: ExamTypes | string | undefined | null,
): string => {
  switch (examType) {
    case ExamTypes.ChapterAssessment:
      return 'Classroom Practice Test';
    case ExamTypes.TermExam:
      return 'Term Assessment';
    default:
      return 'Classroom Practice Test';
  }
};

export const formatBlanks = (
  text: string,
  isMathJax: boolean = false,
): string => {
  if (isMathJax) {
    return text?.replace(
      /(\$[^\$]*\$)|(_+)/g,
      (match, mathContent, underscores) => {
        if (mathContent) {
          return mathContent;
        }
        return `$\\underline{\\hspace{2.5cm}}$`;
      },
    );
  }
  return text?.replace(/(_+)/g, '`$1`');
};

export const pdfWorker = {
  workerSrc: 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js',
  instance: null as Worker | null,
  initialize: async () => {
    if (!pdfWorker.instance) {
      try {
        if ('caches' in window) {
          const cache = await caches.open('pdf-worker-cache');
          const response = await fetch(pdfWorker.workerSrc);
          if (response.ok) {
            await cache.put(pdfWorker.workerSrc, response.clone());
          }
        }
      } catch (error) {
        // TODO: Error handling
      }
    }
    return pdfWorker.workerSrc;
  },
};

export const preloadPdfWorker = async () => {
  await pdfWorker.initialize();
};

export const toCamelCase = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

export const toSnakeCase = (str: string): string => {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase();
};

export const toPascalWithSpaceUniversal = (input: string): string => {
  if (!input) return '';
  return (
    input
      // Convert snake_case to space-separated words
      .replace(/_/g, ' ')
      // Add space between camelCase or PascalCase transitions
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      // Split, capitalize, and join
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
};
